import { ArrowCircleRight, Inventory } from '@mui/icons-material';
import {
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridRowId,
} from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { ClientType, InsurerType } from '../../../../types';
import {
  GridColumnFields,
  ReleasableHoldReasons,
} from '../../bill-grid-components/bill-management-types';
import {
  getBillAmountColumn,
  getBillingTypeColumn,
  getClaimIdColumn,
  getClientColumn,
  getDateOfServiceColumn,
  getHoldReasonColumn,
  getInsurerColumn,
  getMoreDetailsAction,
  getPartnershipColumn,
  getSourceIdColumn,
  getSubmissionStatusColumn,
  getUserIdColumn,
} from '../../bill-grid-components/shared-columns';

interface UseBillColumnsProps {
  clientsData: ClientType[];
  insurersData: Omit<InsurerType, 'eligibilityRef' | 'memberIdRequired'>[];
  moreDetails: (id: GridRowId) => () => void;
  releaseBill: (id: GridRowId) => () => void;
  archiveBill: (id: GridRowId) => () => void;
  submitted: boolean;
  canWrite: boolean;
}

export const useBillColumns = ({
  clientsData,
  insurersData,
  moreDetails,
  releaseBill,
  archiveBill,
  submitted,
  canWrite,
}: UseBillColumnsProps): GridColDef[] =>
  useMemo(
    () => [
      getClaimIdColumn(),
      getHoldReasonColumn(),
      getBillAmountColumn(),
      getUserIdColumn(),
      getSourceIdColumn(),
      getDateOfServiceColumn(),
      getClientColumn(clientsData),
      getInsurerColumn(insurersData),
      getPartnershipColumn(),
      getBillingTypeColumn(),
      getSubmissionStatusColumn(),
      {
        field: GridColumnFields.ACTIONS,
        headerName: 'Actions',
        type: 'actions',
        align: 'right',
        width: 100,
        getActions: (params: GridRenderCellParams): React.ReactElement[] => {
          const actions = [getMoreDetailsAction(params.id, moreDetails)];

          // release holds (write-only)
          if (
            canWrite &&
            Object.values(ReleasableHoldReasons).includes(
              params.row.holdDetails?.holdType,
            )
          ) {
            actions.push(
              <GridActionsCellItem
                key={`${params.id}-release`}
                label="Release Holds"
                icon={<ArrowCircleRight />}
                onClick={releaseBill(params.id)}
                showInMenu={false}
              />,
            );
          }
          // archive bills (write-only)
          if (canWrite && !submitted) {
            actions.push(
              <GridActionsCellItem
                key={`${params.id}-archive`}
                label="Archive"
                icon={<Inventory />}
                onClick={archiveBill(params.id)}
                showInMenu={false}
              />,
            );
          }

          return actions;
        },
      },
    ],
    [
      clientsData,
      insurersData,
      moreDetails,
      submitted,
      releaseBill,
      archiveBill,
      canWrite,
    ],
  );
